import PptxGenJS from 'pptxgenjs';
import * as echarts from 'echarts';
import { captureChart } from './chartCapture';
import { buildOverviewKpis, formatExportCost, formatNL } from './kpiLabels';
import { CHART_TITLES } from './chartTitles';
import {
  chartPaletteLight,
  seriesPrimaryLight,
  seriesSecondaryLight,
  noHash,
} from '../../../utils/chartColors';
import {
  CpiDashboardData,
  EmissionsByMonthResponse,
  EmissionSourceDistributionResponse,
  IntensityPerTonMonthlyResponse,
  IntensityPerTonKmMonthlyResponse,
  IntensityByOrderResponse,
  IntensityByVehicleResponse,
  IntensityByModalityResponse,
  WeightByModalityResponse,
} from '../types';

// -- Shared slide constants --
// Slide dimensions for LAYOUT_16x9: 10" wide x 5.63" tall

// pptxgenjs colors (NO leading '#'), re-pointed to brand light values.
const COLORS = {
  darkBlue: noHash(seriesPrimaryLight()), // '1C9AD6'
  gold: noHash(chartPaletteLight()[2]), // brand amber
  white: 'FFFFFF',
  black: '1E2530', // brand neutral text
  grey: '5E5952', // brand neutral muted
  lightGrey: 'F4F2EE', // brand neutral surface
};

// Neutral border for pptx (NO '#') and echarts option strings (WITH '#').
const BORDER = noHash('#E4E0D8'); // 'E4E0D8'
const BORDER_HASH = '#E4E0D8';

const FONT = {
  face: 'Segoe UI',
  titleSize: 20,
  subtitleSize: 12,
  bodySize: 10,
  smallSize: 8,
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Consumed only in echarts itemStyle.color -> keep WITH '#'.
const MODALITY_COLORS = chartPaletteLight();

// Consumed only in echarts itemStyle.color -> keep WITH '#'.
const SOURCE_COLORS: Record<string, string> = (() => {
  const p = chartPaletteLight();
  return {
    FUEL: p[2],
    CPI: p[0],
    MODALITY: p[1],
    NONE: '#5E5952',
  };
})();

const formatValue = (value: number): string => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return String(Math.round(value));
};

// formatNL and the Overview KPI labels live in ./kpiLabels so they can be
// tested without dragging pptxgenjs and echarts into the test run


// Layout constants — slide is 10" x 5.63"
const HEADER_Y = 0.1;

/** Footer placed on all slides */
function addSlideFooter(slide: PptxGenJS.Slide): void {
  slide.addText('Powered by Loginex', {
    x: 7.5,
    y: 5.25,
    w: 2.2,
    h: 0.3,
    fontSize: FONT.smallSize,
    fontFace: FONT.face,
    color: COLORS.grey,
    align: 'right',
  });
}

/** Standard header placed on content slides */
function addSlideHeader(
  slide: PptxGenJS.Slide,
  title: string,
  clientName: string,
  startDate: string,
  endDate: string
): void {
  slide.addText(title, {
    x: 0.4,
    y: HEADER_Y,
    w: 5,
    h: 0.4,
    fontSize: FONT.titleSize,
    fontFace: FONT.face,
    color: COLORS.black,
    bold: true,
  });

  const headerRows: PptxGenJS.TableRow[] = [
    [
      { text: 'Geselecteerde klant', options: { color: COLORS.grey, fontSize: FONT.smallSize } },
      { text: clientName, options: { color: COLORS.black, fontSize: FONT.smallSize, bold: true } },
    ],
    [
      { text: 'Rapportageperiode vanaf', options: { color: COLORS.grey, fontSize: FONT.smallSize } },
      { text: startDate, options: { color: COLORS.black, fontSize: FONT.smallSize, bold: true } },
    ],
    [
      { text: 'Rapportageperiode tot en met', options: { color: COLORS.grey, fontSize: FONT.smallSize } },
      { text: endDate, options: { color: COLORS.black, fontSize: FONT.smallSize, bold: true } },
    ],
  ];

  slide.addTable(headerRows, {
    x: 5.8,
    y: HEADER_Y,
    w: 3.9,
    colW: [2.4, 1.5],
    rowH: [0.2, 0.2, 0.2],
    border: { type: 'none' },
    fontSize: FONT.smallSize,
    fontFace: FONT.face,
  });
}

// ===== SLIDE 1: COVER =====

export interface CoverSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  logoUrl?: string;
}

async function fetchImageAsBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/** Read a data-URL image's natural pixel dimensions (for aspect-ratio sizing). */
function getImageSize(dataUrl: string): Promise<{ w: number; h: number } | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function addCoverSlide(pptx: PptxGenJS, data: CoverSlideData): Promise<void> {
  const slide = pptx.addSlide();

  slide.addText('Klantrapportage', {
    x: 2.5,
    y: 2.3,
    w: 7,
    h: 0.8,
    fontSize: 28,
    fontFace: FONT.face,
    color: COLORS.black,
    bold: true,
  });

  const infoRows: PptxGenJS.TableRow[] = [
    [
      { text: 'Klantnaam', options: { color: COLORS.grey, fontSize: FONT.subtitleSize } },
      { text: data.clientName, options: { color: COLORS.black, fontSize: FONT.subtitleSize, bold: true } },
    ],
    [
      { text: 'Rapportageperiode vanaf', options: { color: COLORS.grey, fontSize: FONT.subtitleSize } },
      { text: data.startDate, options: { color: COLORS.black, fontSize: FONT.subtitleSize, bold: true } },
    ],
    [
      { text: 'Rapportageperiode tot en met', options: { color: COLORS.grey, fontSize: FONT.subtitleSize } },
      { text: data.endDate, options: { color: COLORS.black, fontSize: FONT.subtitleSize, bold: true } },
    ],
  ];

  slide.addTable(infoRows, {
    x: 2.5,
    y: 3.3,
    w: 6,
    colW: [3, 3],
    border: { type: 'none' },
    fontFace: FONT.face,
  });

  slide.addShape('line', {
    x: 2.5,
    y: 4.4,
    w: 5,
    h: 0,
    line: { color: COLORS.grey, width: 2 },
  });

  slide.addText('Berekend op basis van GCD en CO2-emissiefactoren.nl', {
    x: 2.5,
    y: 4.6,
    w: 5,
    h: 0.3,
    fontSize: FONT.smallSize,
    fontFace: FONT.face,
    color: COLORS.grey,
  });

  // Organization logo — pre-fetch as base64 for browser compatibility with pptxgenjs
  const logoBase64 = data.logoUrl ? await fetchImageAsBase64(data.logoUrl) : null;
  if (logoBase64) {
    // Fit the logo inside a max box, preserving aspect ratio and anchoring the
    // top-left so it stays left-aligned with the title. A wide/wordmark logo
    // uses the full width it needs (up to LOGO_MAX_W); a square logo stays square.
    const LOGO_MAX_W = 3.5;
    const LOGO_MAX_H = 1.4;
    let w = 1.5;
    let h = 1.5;
    const size = await getImageSize(logoBase64);
    if (size && size.w > 0 && size.h > 0) {
      const scale = Math.min(LOGO_MAX_W / size.w, LOGO_MAX_H / size.h);
      w = size.w * scale;
      h = size.h * scale;
    }
    slide.addImage({
      data: logoBase64,
      x: 2.5,
      y: 0.6,
      w,
      h,
    });
  } else {
    slide.addShape('rect', {
      x: 2.5,
      y: 0.6,
      w: 1.5,
      h: 1.5,
      fill: { color: COLORS.lightGrey },
      line: { color: BORDER, width: 1 },
    });
    slide.addText('Logo', {
      x: 2.5,
      y: 0.6,
      w: 1.5,
      h: 1.5,
      fontSize: FONT.bodySize,
      fontFace: FONT.face,
      color: COLORS.grey,
      align: 'center',
      valign: 'middle',
    });
  }

  addSlideFooter(slide);
}

// ===== SLIDE 2: OVERVIEW (KPIs + Emission Source pie chart) =====

export interface OverviewSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  dashboard: CpiDashboardData;
  emissionSource: EmissionSourceDistributionResponse | null;
}

export function addOverviewSlide(pptx: PptxGenJS, data: OverviewSlideData): void {
  const slide = pptx.addSlide();
  const d = data.dashboard;

  addSlideHeader(slide, 'Overzicht', data.clientName, data.startDate, data.endDate);

  // -- KPI Summary Cards --
  const kpiRow: PptxGenJS.TableRow = buildOverviewKpis(d).map((kpi) => ({
    text: [
      { text: kpi.value, options: { fontSize: 14, bold: true, color: COLORS.black } },
      // the unit trails the amount as a caption, as it does on screen
      ...(kpi.unit
        ? [{ text: ` ${kpi.unit}`, options: { fontSize: 8, color: COLORS.grey } }]
        : []),
      { text: '\n', options: { fontSize: 14 } },
      { text: kpi.label, options: { fontSize: 7, color: COLORS.grey } },
    ],
    options: {
      align: 'center' as const,
      valign: 'middle' as const,
      border: { type: 'solid' as const, color: BORDER, pt: 1 },
      fill: { color: COLORS.white },
    },
  }));

  slide.addTable([kpiRow], {
    x: 0.3,
    y: 1.0,
    w: 9.4,
    h: 0.75,
    colW: [1.57, 1.57, 1.57, 1.57, 1.57, 1.57],
    fontFace: FONT.face,
  });

  // -- Emission Source Distribution pie chart (50% width) --
  if (data.emissionSource && data.emissionSource.sources.length > 0) {
    const pieOption: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: 'Emission Source Distribution', left: 10, top: 5, textStyle: { fontSize: 12, fontWeight: 'bold' } },
      legend: {
        orient: 'horizontal',
        bottom: '0%',
        left: 'center',
        textStyle: { color: '#333', fontSize: 11 },
      },
      series: [{
        type: 'pie',
        radius: ['30%', '60%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: true,
        itemStyle: { borderRadius: 4, borderColor: '#fff', borderWidth: 2 },
        label: {
          show: true,
          formatter: (params: any) => {
            const source = data.emissionSource?.sources.find((s) => s.emissionSource === params.name);
            return `${source?.percentage ?? 0}%`;
          },
          color: '#333',
          fontSize: 11,
        },
        data: data.emissionSource.sources.map((s) => ({
          name: s.emissionSource,
          value: s.wtwKg,
          itemStyle: { color: SOURCE_COLORS[s.emissionSource] || '#ccc' },
        })),
      }],
    };

    const img = captureChart(pieOption, { width: 420, height: 280 });
    slide.addImage({ data: img, x: 0.3, y: 2.0, w: 4.7, h: 3.13 });
  }

  addSlideFooter(slide);
}

// ===== SLIDE 3: EMISSIONS (matrix table + CO2e per month chart) =====

export interface EmissionsSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  dashboard: CpiDashboardData;
  emissionsByMonth: EmissionsByMonthResponse;
}

export function addEmissionsSlide(pptx: PptxGenJS, data: EmissionsSlideData): void {
  const slide = pptx.addSlide();
  const d = data.dashboard;

  addSlideHeader(slide, 'Emissies', data.clientName, data.startDate, data.endDate);

  // -- Emissions Matrix Table --
  const hub = d.hubEmissions;
  const formatHub = (kg: number | null): string => (kg == null ? '-' : formatNL(kg / 1000, 3));
  const hubCost =
    hub?.wtwKg != null ? formatExportCost((hub.wtwKg / 1000) * d.co2PricePerTon) : '-';

  const matrixHeader: PptxGenJS.TableRow = [
    { text: '', options: { fill: { color: COLORS.white } } },
    { text: 'WTW', options: { align: 'center' as const, bold: true, fill: { color: COLORS.gold }, color: COLORS.black } },
    { text: 'WTT', options: { align: 'center' as const, bold: true, fill: { color: COLORS.gold }, color: COLORS.black } },
    { text: 'TTW', options: { align: 'center' as const, bold: true, fill: { color: COLORS.gold }, color: COLORS.black } },
    { text: 'Indicatieve kosten', options: { align: 'center' as const, bold: true, fill: { color: COLORS.gold }, color: COLORS.black } },
  ];

  const matrixTransport: PptxGenJS.TableRow = [
    { text: 'Transport', options: { fill: { color: COLORS.gold }, bold: true } },
    { text: formatNL((d.transportEmissions?.wtwKg ?? 0) / 1000, 3), options: { align: 'center' as const } },
    { text: formatNL((d.transportEmissions?.wttKg ?? 0) / 1000, 3), options: { align: 'center' as const } },
    { text: formatNL((d.transportEmissions?.ttwKg ?? 0) / 1000, 3), options: { align: 'center' as const } },
    { text: formatExportCost(((d.transportEmissions?.wtwKg ?? 0) / 1000) * d.co2PricePerTon), options: { align: 'center' as const } },
  ];

  const matrixHub: PptxGenJS.TableRow = [
    { text: 'Hub', options: { fill: { color: COLORS.gold }, bold: true } },
    { text: formatHub(hub?.wtwKg ?? null), options: { align: 'center' as const } },
    { text: formatHub(hub?.wttKg ?? null), options: { align: 'center' as const } },
    { text: formatHub(hub?.ttwKg ?? null), options: { align: 'center' as const } },
    { text: hubCost, options: { align: 'center' as const } },
  ];

  const matrixTotal: PptxGenJS.TableRow = [
    { text: 'Totaal', options: { fill: { color: COLORS.gold }, bold: true } },
    { text: formatNL(d.totalEmissionWtwKg / 1000, 3), options: { align: 'center' as const, bold: true } },
    { text: formatNL(d.totalEmissionWttKg / 1000, 3), options: { align: 'center' as const, bold: true } },
    { text: formatNL(d.totalEmissionTtwKg / 1000, 3), options: { align: 'center' as const, bold: true } },
    { text: formatExportCost(d.indicativeCostEuros), options: { align: 'center' as const, bold: true } },
  ];

  slide.addTable([matrixHeader, matrixTransport, matrixHub, matrixTotal], {
    x: 1.25,
    y: 1.0,
    w: 7.5,
    colW: [1.0, 1.63, 1.63, 1.63, 1.63],
    rowH: [0.2, 0.2, 0.2, 0.2],
    border: { type: 'solid', color: BORDER, pt: 0.5 },
    fontFace: FONT.face,
    fontSize: FONT.smallSize,
  });

  // -- CO2e Total per Month Chart --
  const monthData = data.emissionsByMonth;

  if (monthData && monthData.data.length > 0) {
    const months = monthData.data.map((item) => {
      const monthIndex = parseInt(item.month.split('-')[1], 10) - 1;
      return MONTH_NAMES[monthIndex] || item.month;
    });
    const currentValues = monthData.data.map((item) => item.currentEmissionsKg);

    const series: echarts.LineSeriesOption[] = [
      {
        type: 'line',
        step: 'end',
        name: 'Huidig Jaar',
        data: currentValues,
        lineStyle: { color: seriesPrimaryLight(), width: 2.5 },
        itemStyle: { color: seriesPrimaryLight() },
        showSymbol: false,
      },
    ];

    if (monthData.compareWithPreviousYear) {
      const previousValues = monthData.data.map((item) => item.previousEmissionsKg ?? 0);
      series.push({
        type: 'line',
        step: 'end',
        name: 'Vorig Jaar',
        data: previousValues,
        lineStyle: { color: seriesSecondaryLight(), width: 2.5 },
        itemStyle: { color: seriesSecondaryLight() },
        showSymbol: false,
      });
    }

    const chartOption: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.emissionsByMonth'], left: 20, top: 10, textStyle: { fontSize: 14, fontWeight: 'bold' } },
      tooltip: { trigger: 'axis' },
      legend: monthData.compareWithPreviousYear
        ? { bottom: 10, textStyle: { fontSize: 12 } }
        : undefined,
      grid: {
        left: '3%',
        right: '4%',
        top: '22%',
        bottom: monthData.compareWithPreviousYear ? '18%' : '8%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { fontSize: 11, color: '#999', rotate: 45 },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'value',
        axisLabel: { formatter: (v: number) => formatValue(v), color: '#999' },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series,
    };

    const chartImage = captureChart(chartOption, { width: 700, height: 230 });
    slide.addImage({
      data: chartImage,
      x: 0.8,
      y: 2.5,
      w: 8.4,
      h: 2.76,
    });
  }

  addSlideFooter(slide);
}

// ===== SLIDE 4: CO2e PERIOD (intensity per ton + per ton.km monthly) =====

export interface Co2ePeriodSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  intensityPerTon: IntensityPerTonMonthlyResponse;
  intensityPerTonKm: IntensityPerTonKmMonthlyResponse;
}

export function addCo2ePeriodSlide(pptx: PptxGenJS, data: Co2ePeriodSlideData): void {
  const slide = pptx.addSlide();
  addSlideHeader(slide, 'CO2 emissie per periode', data.clientName, data.startDate, data.endDate);

  // -- CO2e per ton (kg) WTW monthly --
  if (data.intensityPerTon && data.intensityPerTon.data.length > 0) {
    const months = data.intensityPerTon.data.map((d) => {
      const monthIndex = parseInt(d.month.split('-')[1], 10) - 1;
      return MONTH_NAMES[monthIndex] || d.month;
    });
    const values = data.intensityPerTon.data.map((d) => d.intensityKgPerTon);

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.intensityPerTon'], left: 10, top: 5, textStyle: { fontSize: 11, fontWeight: 'bold' } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { fontSize: 10, color: '#999', rotate: 45 },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'CO\u2082e per ton (kg)',
        nameTextStyle: { color: '#999', fontSize: 9 },
        axisLabel: { color: '#999', formatter: (v: number) => v.toFixed(1) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series: [{
        type: 'line',
        step: 'end',
        data: values,
        lineStyle: { color: seriesPrimaryLight(), width: 2.5 },
        itemStyle: { color: seriesPrimaryLight() },
        showSymbol: false,
      }],
    };

    const img = captureChart(option, { width: 400, height: 280 });
    slide.addImage({ data: img, x: 0.3, y: 1.0, w: 4.5, h: 3.15 });
  }

  // -- CO2e per ton.km (kg) WTW monthly --
  if (data.intensityPerTonKm && data.intensityPerTonKm.data.length > 0) {
    const months = data.intensityPerTonKm.data.map((d) => {
      const monthIndex = parseInt(d.month.split('-')[1], 10) - 1;
      return MONTH_NAMES[monthIndex] || d.month;
    });
    const values = data.intensityPerTonKm.data.map((d) => d.intensityKgPerTonKm);

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.intensityPerTonKm'], left: 10, top: 5, textStyle: { fontSize: 11, fontWeight: 'bold' } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: months,
        axisLabel: { fontSize: 10, color: '#999', rotate: 45 },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: CHART_TITLES['cpi.analytics.intensityPerTonKm'],
        nameTextStyle: { color: '#999', fontSize: 9 },
        axisLabel: { color: '#999', formatter: (v: number) => v.toFixed(3) },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series: [{
        type: 'line',
        step: 'end',
        data: values,
        lineStyle: { color: seriesPrimaryLight(), width: 2.5 },
        itemStyle: { color: seriesPrimaryLight() },
        showSymbol: false,
      }],
    };

    const img = captureChart(option, { width: 400, height: 280 });
    slide.addImage({ data: img, x: 5.2, y: 1.0, w: 4.5, h: 3.15 });
  }

  addSlideFooter(slide);
}

// ===== SLIDE 5: CO2e DETAIL (by order, vehicle, modality) =====

export interface Co2eDetailSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  intensityByOrder: IntensityByOrderResponse;
  intensityByVehicle: IntensityByVehicleResponse;
  intensityByModality: IntensityByModalityResponse;
}

export function addCo2eDetailSlide(pptx: PptxGenJS, data: Co2eDetailSlideData): void {
  const slide = pptx.addSlide();
  addSlideHeader(slide, 'CO2 emissie', data.clientName, data.startDate, data.endDate);

  // -- Intensity by Vehicle (top 10) --
  if (data.intensityByVehicle && data.intensityByVehicle.data.length > 0) {
    const items = data.intensityByVehicle.data.slice(0, 10);
    const vehicles = items.map((d) =>
      d.vehicleId.length > 15 ? `${d.vehicleId.substring(0, 15)}...` : d.vehicleId
    );
    const values = items.map((d) => d.intensityKgPerTonKm);

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.intensityByVehicle'], left: 10, top: 5, textStyle: { fontSize: 9 } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '10%', containLabel: true },
      xAxis: { type: 'category', data: vehicles, axisLabel: { fontSize: 8, color: '#666', rotate: 30 } },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#666', formatter: (v: number) => v.toFixed(3) },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series: [{
        type: 'bar',
        data: values,
        itemStyle: { color: seriesPrimaryLight() },
        barMaxWidth: 30,
        label: { show: true, position: 'top', fontSize: 7, formatter: (p: any) => Number(p.value).toFixed(4) },
      }],
    };
    const img = captureChart(option, { width: 400, height: 200 });
    slide.addImage({ data: img, x: 0.3, y: 1.0, w: 4.5, h: 2.25 });
  }

  // -- Intensity by Order (top 10) --
  if (data.intensityByOrder && data.intensityByOrder.data.length > 0) {
    const items = data.intensityByOrder.data.slice(0, 10);
    const orders = items.map((d) =>
      d.transportOrderNumber.length > 10 ? `${d.transportOrderNumber.substring(0, 10)}...` : d.transportOrderNumber
    );
    const values = items.map((d) => d.intensityKgPerTonKm);

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.intensityByOrder'], left: 10, top: 5, textStyle: { fontSize: 9 } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '10%', containLabel: true },
      xAxis: { type: 'category', data: orders, axisLabel: { fontSize: 8, color: '#666', rotate: 30 } },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#666', formatter: (v: number) => v.toFixed(3) },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series: [{
        type: 'bar',
        data: values,
        itemStyle: { color: seriesPrimaryLight() },
        barMaxWidth: 30,
        label: { show: true, position: 'top', fontSize: 7, formatter: (p: any) => Number(p.value).toFixed(4) },
      }],
    };
    const img = captureChart(option, { width: 400, height: 200 });
    slide.addImage({ data: img, x: 5.2, y: 1.0, w: 4.5, h: 2.25 });
  }

  // -- Intensity by Modality --
  if (data.intensityByModality && data.intensityByModality.data.length > 0) {
    const items = data.intensityByModality.data;
    const modalities = items.map((d) => d.modality);
    const values = items.map((d) => d.intensityKgPerTon);

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.intensityByModality'], left: 10, top: 5, textStyle: { fontSize: 9 } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '10%', containLabel: true },
      xAxis: { type: 'category', data: modalities, axisLabel: { fontSize: 9, color: '#666', rotate: 30 } },
      yAxis: {
        type: 'value',
        axisLabel: { color: '#666', formatter: (v: number) => v.toFixed(1) },
        splitLine: { lineStyle: { type: 'dotted', color: BORDER_HASH } },
      },
      series: [{
        type: 'bar',
        data: values.map((v, i) => ({
          value: v,
          itemStyle: { color: MODALITY_COLORS[i % MODALITY_COLORS.length] },
        })),
        barMaxWidth: 40,
        label: { show: true, position: 'top', fontSize: 8, formatter: (p: any) => Number(p.value).toFixed(2) },
      }],
    };
    const img = captureChart(option, { width: 400, height: 200 });
    slide.addImage({ data: img, x: 0.3, y: 3.35, w: 4.5, h: 2.25 });
  }

  addSlideFooter(slide);
}

// ===== SLIDE 6: OPERATIONAL (weight by modality) =====

export interface OperationalSlideData {
  clientName: string;
  startDate: string;
  endDate: string;
  weightByModality: WeightByModalityResponse;
}

export function addOperationalSlide(pptx: PptxGenJS, data: OperationalSlideData): void {
  const slide = pptx.addSlide();
  addSlideHeader(slide, 'CO2 emissie per periode', data.clientName, data.startDate, data.endDate);

  if (data.weightByModality && data.weightByModality.data.length > 0) {
    const monthsSet = new Set<string>();
    const modalitiesSet = new Set<string>();
    const dataMap = new Map<string, Map<string, number>>();

    data.weightByModality.data.forEach((item) => {
      monthsSet.add(item.month);
      modalitiesSet.add(item.modality);
      if (!dataMap.has(item.month)) dataMap.set(item.month, new Map());
      const monthMap = dataMap.get(item.month);
      if (monthMap) monthMap.set(item.modality, item.weightTons);
    });

    const months = Array.from(monthsSet).sort();
    const modalities = Array.from(modalitiesSet);

    const monthLabels = months.map((m) => {
      const monthIndex = parseInt(m.split('-')[1], 10) - 1;
      return MONTH_NAMES[monthIndex] || m;
    });

    const series: echarts.BarSeriesOption[] = modalities.map((modality, idx) => ({
      type: 'bar',
      name: modality,
      data: months.map((m) => dataMap.get(m)?.get(modality) ?? 0),
      itemStyle: { color: MODALITY_COLORS[idx % MODALITY_COLORS.length] },
      barMaxWidth: 35,
      label: {
        show: true,
        position: 'top',
        fontSize: 8,
        formatter: (params: any) => (params.value > 0 ? String(Math.round(params.value)) : ''),
      },
    }));

    const option: echarts.EChartsOption = {
      backgroundColor: '#ffffff',
      title: { text: CHART_TITLES['cpi.analytics.weightByModality'], left: 20, top: 10, textStyle: { fontSize: 13, fontWeight: 'bold' } },
      tooltip: { trigger: 'axis' },
      legend: { bottom: 10, textStyle: { fontSize: 10 } },
      grid: { left: '3%', right: '4%', top: '22%', bottom: '18%', containLabel: true },
      xAxis: { type: 'category', data: monthLabels, axisLabel: { fontSize: 10, color: '#666' } },
      yAxis: { type: 'value', axisLabel: { color: '#666' } },
      series,
    };

    const img = captureChart(option, { width: 800, height: 300 });
    slide.addImage({ data: img, x: 0.5, y: 1.0, w: 9.0, h: 3.375 });
  }

  addSlideFooter(slide);
}
